import type { NodeAPIData } from 'types/catalog_types';
import type { TextBounds, TextNodeEntity } from './text_nodes.types';

const [fontMinSize, fontMaxSize] = [4, 300];

/**
 * These are all imported from Google Fonts. For order-api to render the preview,
 * every available font needs included in the build image. So any change to this
 * list *MUST* be reflected there as well.
 */
const fontList = [
  'Anton',
  'Architects Daughter',
  'Arvo',
  'Bree Serif',
  'Cinzel',
  'Courgette',
  'Dancing Script',
  'Inconsolata',
  'Indie Flower',
  'Josefin Slab',
  'Kalam',
  'Kaushan Script',
  'Lobster',
  'Merriweather',
  'Noto Serif',
  'Oswald',
  'PT Sans',
  'Pacifico',
  'Raleway',
  'Righteous',
  'Roboto Condensed',
  'Satisfy',
  'Shadows Into Light',
  'Source Sans Pro',
] as const;

const textNodeDefaults: Omit<TextNodeEntity, 'id'> = {
  fontSize: '12',
  fontFamily: 'Merriweather',
  horizontalAlignment: 'middle',
  verticalAlignment: 'middle',
  textColor: '#626262',
  textContent: 'Text Here',
  autofit: true,
};

/** This is used as the id for a text node's svg, followed by the node uid */
const textNodeSVGBaseId = 'text_node--svg_';

function isTextNode(node: NodeAPIData) {
  return node.node_type === 3;
}

/**
 * Measures the text nodes within an SVG container
 *
 * If the text is oversized or undersized by more than the limit,
 * return a value for correcting the font size to fit the text
 * within the container
 *
 * If the text is rightsized, return null
 *
 */
const FIT_LIMIT = 0.1;
function getTextAutofitCorrection(svg: SVGSVGElement) {
  const { height, width } = svg.getBoundingClientRect();
  const { height: textHeight, width: textWidth } = getSVGTextBounds(svg);

  // Break early so we don't divide by 0
  if (textHeight === 0 || textWidth === 0) return null;

  const widthRatio = width / textWidth;
  const heightRatio = height / textHeight;

  // If either are oversized, correct for that first
  if (widthRatio < 1 || heightRatio < 1) {
    return Math.min(widthRatio, heightRatio);
  }

  // By here we know that both are undersized, so figure out
  // which is closest to the correct size
  const closestRatio = Math.min(widthRatio, heightRatio);

  // If the closest dimension is within the given limits of the container, consider it rightsized
  if (closestRatio - 1 < FIT_LIMIT) {
    return null;
  }

  return closestRatio;
}

function getSVGTextBounds(svg: SVGSVGElement) {
  const textNodes = svg.querySelectorAll('text');
  return Array.from(textNodes).reduce(
    (o: TextBounds, txtNode) => {
      const { width: txtWidth, height: txtHeight } = txtNode.getBoundingClientRect();
      const yAttr = txtNode.getAttribute('y');
      const txtTop = yAttr ? parseFloat(yAttr) : 0;
      let fontHeight = 0;

      // This is wrapped in a try-catch due to testing limitations, JSDom does not support svg methods
      // Behaves correctly in browser environment
      try {
        fontHeight = txtNode.getBBox().height;
      } catch {}

      return {
        top: o.top || txtTop,
        bottom: txtTop + txtHeight,
        width: Math.max(o.width, txtWidth),
        height: textNodes.length * fontHeight,
      };
    },
    {
      top: undefined,
      bottom: undefined,
      width: 0,
      height: 0,
    }
  );
}

export default {
  fontMinSize,
  fontMaxSize,
  fontList,
  textNodeDefaults,
  textNodeSVGBaseId,
  isTextNode,
  getTextAutofitCorrection,
  getSVGTextBounds,
};
